'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetWinnersRow, ItemsRenderProps } from '@morfi/backend-sdk-v2/client';
import { useGetWinnersInfinite } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { EmptyState } from '@/components/ui/empty-state';
import { Icon } from '@/components/ui/icon';
import { Award01IconSolid, Loading03Icon, SearchRemoveIcon } from '@/components/ui/icon/glyphs';
import { Panel } from '@/components/ui/panel';
import { useRoutePerf } from '@/hooks/use-route-perf';
import { PROPOSAL_FEED_LIST_PANEL_FILL_CLASS } from '@/lib/ui/layout';
import { cn } from '@/lib/ui/utils';
import { WinnersFeedSkeleton } from './winners-feed-skeleton';
import { WINNERS_FEED_DEFAULT_SORT, type WinnersFeedSortKey } from './winners-feed-sorts';
import { WINNERS_ROW_GRID_CLASS } from './winners-grid';
import { WinnersRow } from './winners-row';

/** Desktop (lg+) column header — hidden below lg; columns match `WINNERS_ROW_GRID_CLASS`. */
function WinnersFeedHeader() {
  const $ = _c(42);
  const t = useTranslations("markets.winners");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = cn("hidden lg:grid", WINNERS_ROW_GRID_CLASS, "items-end px-4 pb-4 pt-4 text-caption font-medium text-foreground-tertiary");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== t) {
    t1 = t("winningOutcome");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <span className="min-w-0 truncate">{t1}</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("price");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <span className="text-center">{t3}</span>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("change1h");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <span className="text-center">{t5}</span>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("change24h");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <span className="text-center">{t7}</span>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t) {
    t9 = t("totalVolume");
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = <span className="text-center">{t9}</span>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t) {
    t11 = t("graduated");
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11) {
    t12 = <span className="text-center">{t11}</span>;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t) {
    t13 = t("marketCap");
    $[25] = t;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13) {
    t14 = <span className="text-center">{t13}</span>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t) {
    t15 = t("last7Days");
    $[29] = t;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t15) {
    t16 = <span className="text-right">{t15}</span>;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t10 || $[34] !== t12 || $[35] !== t14 || $[36] !== t16 || $[37] !== t2 || $[38] !== t4 || $[39] !== t6 || $[40] !== t8) {
    t17 = <div className={t0} data-testid="winners-feed-header">{t2}{t4}{t6}{t8}{t10}{t12}{t14}{t16}</div>;
    $[33] = t10;
    $[34] = t12;
    $[35] = t14;
    $[36] = t16;
    $[37] = t2;
    $[38] = t4;
    $[39] = t6;
    $[40] = t8;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  return t17;
}

/**
 * The Winners feed — indexed winning outcomes (Figma node 3682-948). Reads
 * `useGetWinnersInfinite` and renders the responsive table: a desktop
 * grid (lg+) and condensed rows (<lg). Static fields ride on each row's
 * snapshot; volatile fields subscribe per-cell in `WinnersRow` so a NATS AMM
 * swap patches only the affected cells. New graduations live-prepend and
 * per-row price/volume live-patch via the SDK's declared realtime recipes.
 */
export interface WinnersFeedProps {
  /** Active sort key (default `createdAt`). Server orders globally. */
  sortBy?: WinnersFeedSortKey;
  /** Sort direction (default `desc`). */
  sortOrder?: 'asc' | 'desc';
  /** When set, restricts the feed to these market pubkeys (watchlist filter). */
  pubkeys?: readonly string[];
  /**
   * Pre-converted winner query params (D6 money, signed bps, ISO
   * dates, CSV category) from the filter-overhaul sheet. Threaded verbatim
   * into `useGetWinnersInfinite`. Empty/absent when the gate is off.
   */
  filterArgs?: Record<string, string>;
}
export function WinnersFeed(t0) {
  const $ = _c(30);
  const {
    sortBy: t1,
    sortOrder: t2,
    pubkeys: filterPubkeys,
    filterArgs
  } = t0;
  const sortBy = t1 === undefined ? WINNERS_FEED_DEFAULT_SORT : t1;
  const sortOrder = t2 === undefined ? "desc" : t2;
  const t = useTranslations("markets.winners");
  const tMarkets = useTranslations("markets");
  let t3;
  if ($[0] !== filterPubkeys) {
    t3 = filterPubkeys !== undefined && filterPubkeys.length > 0 ? {
      pubkeys: filterPubkeys.join(",")
    } : {};
    $[0] = filterPubkeys;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== filterArgs) {
    t4 = filterArgs ?? {};
    $[2] = filterArgs;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== sortBy || $[5] !== sortOrder || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      sortBy,
      sortOrder,
      viewportGated: true,
      ...t3,
      ...t4
    };
    $[4] = sortBy;
    $[5] = sortOrder;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const q = useGetWinnersInfinite(t5);
  const {
    Items,
    ids,
    rows,
    isPending,
    isError,
    hasNextPage,
    isFetchingNextPage
  } = q;
  const t6 = !isPending;
  let t7;
  if ($[9] !== t6) {
    t7 = {
      name: "winners",
      isReady: t6
    };
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  useRoutePerf(t7);
  let indexByMint;
  let snapshotByMint;
  if ($[11] !== rows) {
    snapshotByMint = new Map();
    indexByMint = new Map();
    for (const r of rows) {
      if (r.mint.length > 0 && !snapshotByMint.has(r.mint)) {
        indexByMint.set(r.mint, snapshotByMint.size);
        snapshotByMint.set(r.mint, r);
      }
    }
    $[11] = rows;
    $[12] = indexByMint;
    $[13] = snapshotByMint;
  } else {
    indexByMint = $[12];
    snapshotByMint = $[13];
  }
  const total = ids.length;
  let t8;
  if ($[14] !== filterArgs || $[15] !== filterPubkeys) {
    t8 = filterPubkeys !== undefined && filterPubkeys.length > 0 || filterArgs !== undefined && Object.keys(filterArgs).length > 0;
    $[14] = filterArgs;
    $[15] = filterPubkeys;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const filtersActive = t8;
  if (isPending) {
    let t9;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div data-testid="winners-feed-loading"><WinnersFeedSkeleton /></div>;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn(PROPOSAL_FEED_LIST_PANEL_FILL_CLASS, "rounded-[calc(var(--radius-card)+0.75rem)] pb-3 pt-0", "max-lg:contents");
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== Items || $[20] !== filtersActive || $[21] !== hasNextPage || $[22] !== indexByMint || $[23] !== isError || $[24] !== isFetchingNextPage || $[25] !== snapshotByMint || $[26] !== t || $[27] !== tMarkets || $[28] !== total) {
    t10 = <Panel surface="solid" inheritBackground={true} className={t9} data-testid="winners-feed">{isError ? <div className="flex flex-col items-center justify-center py-24 text-center"><h2 className="text-heading">{t("errorTitle")}</h2></div> : total === 0 ? filtersActive ? <EmptyState size="page" icon={SearchRemoveIcon} title={tMarkets("noResults")} /> : <EmptyState size="page" icon={Award01IconSolid} title={t("empty.title")} description={t("empty.body")} /> : <div className="flex flex-col gap-0"><WinnersFeedHeader /><ul className="flex flex-col gap-2"><Items>{t11 => {
              const {
                ref,
                id
              } = t11;
              const snapshot = snapshotByMint.get(id);
              return <li key={id} ref={ref as unknown as React.Ref<HTMLLIElement>} className="min-w-0">{snapshot !== undefined && <WinnersRow mint={id} snapshot={snapshot} priorityImage={(indexByMint.get(id) ?? 0) < 8} />}</li>;
            }}</Items></ul>{hasNextPage && isFetchingNextPage && <div className="flex justify-center py-4"><Icon icon={Loading03Icon} className="size-6 animate-spin text-foreground-secondary" /></div>}</div>}</Panel>;
    $[19] = Items;
    $[20] = filtersActive;
    $[21] = hasNextPage;
    $[22] = indexByMint;
    $[23] = isError;
    $[24] = isFetchingNextPage;
    $[25] = snapshotByMint;
    $[26] = t;
    $[27] = tMarkets;
    $[28] = total;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}